/**
 * Owns Step6 page wiring and state subscriptions. Business decisions, request
 * construction and Step03 writeback stay in the existing injected controllers.
 */
export function canCompleteRevision({
  revisionSuggestionsMeta = null,
  diagnosisInFlight = false,
} = {}) {
  if (diagnosisInFlight) return false;
  const authority = revisionSuggestionsMeta?.executionAuthority || {};
  // The backend Step04 audit owns this conclusion. The UI only consumes the
  // typed result; it must never turn a needs_revision/facts_required response
  // into a cosmetic "完成" state.
  return authority.fullTextReviewFailed !== true
    && String(authority.conclusion || '').trim().toLowerCase() === 'pass';
}

export function createRevisionInitializationController({
  select,
  setState,
  subscribe,
  applyRevisionSuggestions,
  openImageSuite,
  getRevisionMode,
  showToast,
  confirmCandidate,
  cancelCandidate,
  previousCandidate,
  suggestionsController,
  masterInstructionsController,
  renderFactPanel,
  syncApplyButton,
  normalizeAdviceText,
  normalizeSupplementalFacts,
  getState,
  setupSearchTermsEditor,
}) {
  let bound = false;

  function syncCompletionButton() {
    const nextButton = select('#step6-next-button');
    if (!nextButton) return;
    const canComplete = canCompleteRevision({
      revisionSuggestionsMeta: getState('revisionSuggestionsMeta'),
      diagnosisInFlight: getState('diagnosisInFlight') === true,
    });
    nextButton.disabled = !canComplete;
    nextButton.title = canComplete
      ? '完整审稿已通过'
      : '仍有必须修改项，或完整审稿尚未通过';
  }

  function bind() {
    if (bound) return;
    select('#apply-revision-button')?.addEventListener('click', applyRevisionSuggestions);
    select('#step6-enter-suite-btn')?.addEventListener('click', openImageSuite);

    const fullRewriteInput = select('#revision-full-rewrite');
    fullRewriteInput?.addEventListener('change', () => {
      setState('revisionMode', getRevisionMode());
      showToast(fullRewriteInput.checked
        ? '已允许全文重写；系统仍会保留未被事实推翻的商品信息。'
        : '已启用字段保护模式；未命中字段会原样保留。');
    });

    select('#step6-prev-button')?.addEventListener('click', () => setState('currentStep', 3));
    const nextButton = select('#step6-next-button');
    if (nextButton) {
      nextButton.textContent = '完成';
      nextButton.addEventListener('click', () => {
        if (!canCompleteRevision({
          revisionSuggestionsMeta: getState('revisionSuggestionsMeta'),
          diagnosisInFlight: getState('diagnosisInFlight') === true,
        })) {
          showToast('仍有必须修改项，或完整审稿尚未通过，不能标记完成。', true);
          return;
        }
        showToast('优化中心已通过完整审稿，请直接进入套图或保存 Listing');
      });
      syncCompletionButton();
    }
    select('#confirm-revision-candidate')?.addEventListener('click', confirmCandidate);
    select('#cancel-revision-candidate')?.addEventListener('click', cancelCandidate);
    select('#previous-revision-candidate')?.addEventListener('click', previousCandidate);

    subscribe('state:revisionSuggestionsChanged', () => { suggestionsController.render(); syncApplyButton(); syncCompletionButton(); });
    subscribe('state:revisionAdviceTextChanged', () => { suggestionsController.render(); syncApplyButton(); syncCompletionButton(); });
    subscribe('state:revisionSuggestionsMetaChanged', () => { renderFactPanel(); syncApplyButton(); syncCompletionButton(); });
    subscribe('state:revisionMissingFactQuestionsChanged', renderFactPanel);
    subscribe('state:revisionSupplementalFactsChanged', renderFactPanel);
    subscribe('state:diagnosisInFlightChanged', () => { syncApplyButton(); syncCompletionButton(); });
    subscribe('state:revisionOverviewChanged', () => { suggestionsController.render(); syncCompletionButton(); });
    masterInstructionsController.bind();
    subscribe('state:masterRevisionInstructionsChanged', () => { masterInstructionsController.sync(); syncApplyButton(); });

    const supplementalFactsInput = select('#revision-supplemental-facts');
    supplementalFactsInput?.addEventListener('input', () => {
      setState('revisionSupplementalFacts', normalizeSupplementalFacts(supplementalFactsInput.value));
      syncApplyButton();
    });
    subscribe('state:currentStepChanged', ({ curr }) => {
      if (curr !== 4) return;
      const suggestions = getState('revisionSuggestions');
      masterInstructionsController.sync();
      suggestionsController.render();
      renderFactPanel();
      syncApplyButton();
      syncCompletionButton();
      if (suggestions?.length || normalizeAdviceText(getState('revisionAdviceText'))) setupSearchTermsEditor();
    });

    masterInstructionsController.sync();
    suggestionsController.render();
    renderFactPanel();
    syncApplyButton();
    syncCompletionButton();
    bound = true;
  }

  return { bind };
}
